import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QualificationForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    
    // Organization Details
    teamSize: '',
    industry: '',
    techStack: [],
    currentMethodology: '',
    
    // Challenge Details
    primaryChallenge: '',
    urgency: '',
    budget: '',
    timeline: '',
    
    // Technical Environment
    cloudProvider: '',
    deploymentFrequency: '',
    testingCoverage: '',
    monitoringTools: '',
    
    // Additional Context
    specificPainPoints: '',
    previousConsulting: '',
    successMetrics: ''
  });

  const [currentSection, setCurrentSection] = useState(0);
  const [errors, setErrors] = useState({});

  const sections = [
    {
      title: 'Contact Information',
      description: 'Let us know who you are and how to reach you',
      fields: ['firstName', 'lastName', 'email', 'phone', 'company', 'title']
    },
    {
      title: 'Organization Details',
      description: 'Help us understand your team and technical environment',
      fields: ['teamSize', 'industry', 'techStack', 'currentMethodology']
    },
    {
      title: 'Challenge Assessment',
      description: 'Describe your current challenges and objectives',
      fields: ['primaryChallenge', 'urgency', 'budget', 'timeline']
    },
    {
      title: 'Technical Environment',
      description: 'Details about your current technical setup',
      fields: ['cloudProvider', 'deploymentFrequency', 'testingCoverage', 'monitoringTools']
    },
    {
      title: 'Additional Context',
      description: 'Any additional information that would help us prepare',
      fields: ['specificPainPoints', 'previousConsulting', 'successMetrics']
    }
  ];

  const teamSizeOptions = [
    { value: '1-10', label: '1-10 engineers' },
    { value: '11-25', label: '11-25 engineers' },
    { value: '26-50', label: '26-50 engineers' },
    { value: '51-100', label: '51-100 engineers' },
    { value: '100+', label: '100+ engineers' }
  ];

  const industryOptions = [
    { value: 'fintech', label: 'Financial Technology' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'ecommerce', label: 'E-commerce & Retail' },
    { value: 'saas', label: 'SaaS & Enterprise Software' },
    { value: 'media', label: 'Media & Entertainment' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'other', label: 'Other' }
  ];

  const techStackOptions = [
    { value: 'react', label: 'React/Next.js' },
    { value: 'vue', label: 'Vue.js/Nuxt.js' },
    { value: 'angular', label: 'Angular' },
    { value: 'node', label: 'Node.js' },
    { value: 'python', label: 'Python/Django/Flask' },
    { value: 'java', label: 'Java/Spring' },
    { value: 'dotnet', label: '.NET/C#' },
    { value: 'go', label: 'Go' },
    { value: 'rust', label: 'Rust' },
    { value: 'php', label: 'PHP/Laravel' }
  ];

  const methodologyOptions = [
    { value: 'agile', label: 'Agile/Scrum' },
    { value: 'kanban', label: 'Kanban' },
    { value: 'waterfall', label: 'Waterfall' },
    { value: 'hybrid', label: 'Hybrid Approach' },
    { value: 'none', label: 'No Formal Methodology' }
  ];

  const challengeOptions = [
    { value: 'velocity', label: 'Slow Development Velocity' },
    { value: 'quality', label: 'Quality & Bug Issues' },
    { value: 'scalability', label: 'Scalability Challenges' },
    { value: 'technical_debt', label: 'Technical Debt Management' },
    { value: 'team_culture', label: 'Team Culture & Ownership' },
    { value: 'architecture', label: 'Architecture Modernization' }
  ];

  const urgencyOptions = [
    { value: 'immediate', label: 'Immediate (Within 30 days)' },
    { value: 'short_term', label: 'Short-term (1-3 months)' },
    { value: 'medium_term', label: 'Medium-term (3-6 months)' },
    { value: 'long_term', label: 'Long-term (6+ months)' }
  ];

  const budgetOptions = [
    { value: 'under_50k', label: 'Under $50,000' },
    { value: '50k_100k', label: '$50,000 - $100,000' },
    { value: '100k_250k', label: '$100,000 - $250,000' },
    { value: '250k_500k', label: '$250,000 - $500,000' },
    { value: 'over_500k', label: 'Over $500,000' }
  ];

  const cloudProviderOptions = [
    { value: 'aws', label: 'Amazon Web Services' },
    { value: 'azure', label: 'Microsoft Azure' },
    { value: 'gcp', label: 'Google Cloud Platform' },
    { value: 'multi', label: 'Multi-cloud' },
    { value: 'on_premise', label: 'On-premise' },
    { value: 'hybrid', label: 'Hybrid Cloud' }
  ];

  const deploymentOptions = [
    { value: 'multiple_daily', label: 'Multiple times per day' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly or less' }
  ];

  const testingCoverageOptions = [
    { value: 'high', label: 'High (>80%)' },
    { value: 'medium', label: 'Medium (50-80%)' },
    { value: 'low', label: 'Low (20-50%)' },
    { value: 'minimal', label: 'Minimal (<20%)' },
    { value: 'none', label: 'No automated testing' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateSection = (sectionIndex) => {
    const section = sections[sectionIndex];
    const newErrors = {};
    
    section.fields.forEach(field => {
      if (!formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0)) {
        newErrors[field] = 'This field is required';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateSection(currentSection)) {
      if (currentSection < sections.length - 1) {
        setCurrentSection(currentSection + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const renderField = (field) => {
    switch (field) {
      case 'firstName':
        return (
          <Input
            label="First Name"
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            error={errors.firstName}
            required
            placeholder="Enter your first name"
          />
        );
      
      case 'lastName':
        return (
          <Input
            label="Last Name"
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            error={errors.lastName}
            required
            placeholder="Enter your last name"
          />
        );
      
      case 'email':
        return (
          <Input
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={errors.email}
            required
            placeholder="your.email@company.com"
          />
        );
      
      case 'phone':
        return (
          <Input
            label="Phone Number"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={errors.phone}
            required
            placeholder="+1 (555) 123-4567"
          />
        );
      
      case 'company':
        return (
          <Input
            label="Company Name"
            type="text"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            error={errors.company}
            required
            placeholder="Your company name"
          />
        );
      
      case 'title':
        return (
          <Input
            label="Job Title"
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="CTO, VP Engineering, etc."
          />
        );
      
      case 'teamSize':
        return (
          <Select
            label="Engineering Team Size"
            options={teamSizeOptions}
            value={formData.teamSize}
            onChange={(value) => handleInputChange('teamSize', value)}
            error={errors.teamSize}
            required
            placeholder="Select team size"
          />
        );
      
      case 'industry':
        return (
          <Select
            label="Industry"
            options={industryOptions}
            value={formData.industry}
            onChange={(value) => handleInputChange('industry', value)}
            error={errors.industry}
            required
            placeholder="Select your industry"
          />
        );
      
      case 'techStack':
        return (
          <Select
            label="Primary Technology Stack"
            options={techStackOptions}
            value={formData.techStack}
            onChange={(value) => handleInputChange('techStack', value)}
            error={errors.techStack}
            required
            multiple
            searchable
            placeholder="Select technologies"
          />
        );
      
      case 'currentMethodology':
        return (
          <Select
            label="Current Development Methodology"
            options={methodologyOptions}
            value={formData.currentMethodology}
            onChange={(value) => handleInputChange('currentMethodology', value)}
            error={errors.currentMethodology}
            required
            placeholder="Select methodology"
          />
        );
      
      case 'primaryChallenge':
        return (
          <Select
            label="Primary Challenge"
            options={challengeOptions}
            value={formData.primaryChallenge}
            onChange={(value) => handleInputChange('primaryChallenge', value)}
            error={errors.primaryChallenge}
            required
            placeholder="Select primary challenge"
          />
        );
      
      case 'urgency':
        return (
          <Select
            label="Timeline Urgency"
            options={urgencyOptions}
            value={formData.urgency}
            onChange={(value) => handleInputChange('urgency', value)}
            error={errors.urgency}
            required
            placeholder="Select urgency"
          />
        );
      
      case 'budget':
        return (
          <Select
            label="Budget Range"
            options={budgetOptions}
            value={formData.budget}
            onChange={(value) => handleInputChange('budget', value)}
            error={errors.budget}
            required
            placeholder="Select budget range"
          />
        );
      
      case 'timeline':
        return (
          <Input
            label="Desired Timeline"
            type="text"
            value={formData.timeline}
            onChange={(e) => handleInputChange('timeline', e.target.value)}
            error={errors.timeline}
            required
            placeholder="e.g., Q2 2025, 6 months, ASAP"
          />
        );
      
      case 'cloudProvider':
        return (
          <Select
            label="Cloud Provider"
            options={cloudProviderOptions}
            value={formData.cloudProvider}
            onChange={(value) => handleInputChange('cloudProvider', value)}
            error={errors.cloudProvider}
            required
            placeholder="Select cloud provider"
          />
        );
      
      case 'deploymentFrequency':
        return (
          <Select
            label="Deployment Frequency"
            options={deploymentOptions}
            value={formData.deploymentFrequency}
            onChange={(value) => handleInputChange('deploymentFrequency', value)}
            error={errors.deploymentFrequency}
            required
            placeholder="Select deployment frequency"
          />
        );
      
      case 'testingCoverage':
        return (
          <Select
            label="Test Coverage"
            options={testingCoverageOptions}
            value={formData.testingCoverage}
            onChange={(value) => handleInputChange('testingCoverage', value)}
            error={errors.testingCoverage}
            required
            placeholder="Select test coverage"
          />
        );
      
      case 'monitoringTools':
        return (
          <Input
            label="Monitoring & Observability Tools"
            type="text"
            value={formData.monitoringTools}
            onChange={(e) => handleInputChange('monitoringTools', e.target.value)}
            error={errors.monitoringTools}
            required
            placeholder="e.g., DataDog, New Relic, Grafana"
          />
        );
      
      case 'specificPainPoints':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              Specific Pain Points
            </label>
            <textarea
              value={formData.specificPainPoints}
              onChange={(e) => handleInputChange('specificPainPoints', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              rows={4}
              placeholder="Describe your specific challenges, bottlenecks, or issues..."
              required
            />
            {errors.specificPainPoints && (
              <p className="text-sm text-error">{errors.specificPainPoints}</p>
            )}
          </div>
        );
      
      case 'previousConsulting':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              Previous Consulting Experience
            </label>
            <textarea
              value={formData.previousConsulting}
              onChange={(e) => handleInputChange('previousConsulting', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              rows={3}
              placeholder="Have you worked with consultants before? What worked or didn't work?"
              required
            />
            {errors.previousConsulting && (
              <p className="text-sm text-error">{errors.previousConsulting}</p>
            )}
          </div>
        );
      
      case 'successMetrics':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              Success Metrics
            </label>
            <textarea
              value={formData.successMetrics}
              onChange={(e) => handleInputChange('successMetrics', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              rows={3}
              placeholder="How will you measure success? What outcomes are you looking for?"
              required
            />
            {errors.successMetrics && (
              <p className="text-sm text-error">{errors.successMetrics}</p>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  const currentSectionData = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">
            Step {currentSection + 1} of {sections.length}
          </span>
          <span className="text-sm text-text-secondary">
            {Math.round(((currentSection + 1) / sections.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-accent h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Section Header */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          {currentSectionData.title}
        </h3>
        <p className="text-text-secondary">
          {currentSectionData.description}
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-6 mb-8">
        <div className="grid md:grid-cols-2 gap-6">
          {currentSectionData.fields.map(field => (
            <div key={field} className={currentSectionData.fields.length === 1 ? 'md:col-span-2' : ''}>
              {renderField(field)}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentSection === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <div className="flex items-center space-x-2">
          {sections.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full ${
                index === currentSection
                  ? 'bg-accent'
                  : index < currentSection
                  ? 'bg-success' :'bg-border'
              }`}
            />
          ))}
        </div>

        <Button
          variant="default"
          onClick={handleNext}
          iconName={isLastSection ? "Send" : "ChevronRight"}
          iconPosition="right"
          className="bg-accent hover:bg-accent/90"
        >
          {isLastSection ? 'Submit Application' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default QualificationForm;